import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChartLine, Target, TrendUp, Brain, ListChecks, Warning, Repeat, BookOpen, ArrowRight, CheckCircle } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../../lib/seo";

const ComoAnalisarTrades = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Como Analisar Seus Trades | Melhore Seus Resultados no Trading',
      description: 'Aprenda como analisar seus trades corretamente e melhorar sua performance no trading com dados e consistência.',
      path: '/pt/como-analisar-trades',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/como-analisar-trades'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/geni-logo.png" alt="GENI" className="h-8" />
            <span className="text-xl font-bold">GENI</span>
          </Link>
          <Link
            to="/signup"
            className="bg-[#00FF85] text-black px-5 py-2 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
          >
            Começar Grátis
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">Análise de Trades</span>
        </nav>

        {/* Category Tag */}
        <div className="flex items-center gap-2 text-[#00FF85] text-sm font-medium mb-4">
          <ChartLine size={16} weight="bold" />
          <span>Análise de Trades</span>
        </div>

        {/* H1 */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
          Como Analisar Seus Trades da Forma Correta
        </h1>

        <p className="text-lg text-gray-400 mb-12">
          (Guia Prático para Traders Brasileiros)
        </p>

        {/* Introdução */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            Muitos traders operam todos os dias. Executam dezenas de trades por semana, passam horas olhando gráficos, mas no final do mês percebem que não evoluíram. O motivo? <strong className="text-white">Não analisam seus trades de forma estruturada.</strong>
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-12">
            Operar sem analisar é como treinar um esporte sem assistir às gravações dos seus jogos. Você pode até melhorar por acidente, mas nunca vai entender o que realmente funciona e o que precisa mudar. A análise de trades transforma experiência em aprendizado real.
          </p>

          {/* Seção 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Target size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Por Que Analisar Trades é Essencial</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-4">
              A evolução no trading vem da análise, não da quantidade de operações. Repetir os mesmos trades sem entender o que funcionou e o que falhou não gera melhoria — gera estagnação.
            </p>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-white mb-4">O ciclo de evolução no trading:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#00FF85] mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Dados geram aprendizado</strong> — cada trade registrado é uma lição em potencial</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#00FF85] mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Padrões emergem com volume</strong> — você descobre seus pontos fortes e fracos</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#00FF85] mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Decisões melhoram</strong> — você para de operar no escuro</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O trader que analisa 100 trades tem uma vantagem brutal sobre o trader que apenas operou 1000 sem olhar para trás. Qualidade de análise supera quantidade de operações.
            </p>
          </section>

          {/* Seção 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <ListChecks size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">O Que Você Deve Analisar em Cada Trade</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Cada trade tem componentes que precisam ser avaliados separadamente. Olhar apenas para o resultado final (lucro ou prejuízo) esconde informações valiosas.
            </p>

            <div className="grid gap-4 mb-6">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">1. Entrada</h3>
                <p className="text-gray-400 text-sm">O preço de entrada foi o ideal? Você entrou cedo demais ou tarde demais? Existia confirmação suficiente?</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">2. Saída</h3>
                <p className="text-gray-400 text-sm">Você saiu no momento certo? Fechou cedo demais por medo ou segurou tempo demais por ganância?</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">3. Risco/Retorno</h3>
                <p className="text-gray-400 text-sm">O trade tinha um RR favorável antes de entrar? O risco estava proporcional ao potencial de ganho?</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">4. Setup Utilizado</h3>
                <p className="text-gray-400 text-sm">Qual foi o setup? Você seguiu as regras do setup ou improvisou? O setup está funcionando historicamente?</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">5. Execução</h3>
                <p className="text-gray-400 text-sm">A execução foi limpa? Houve slippage? Você gerenciou a posição corretamente durante o trade?</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Um trade perdedor com execução perfeita é melhor que um trade vencedor com execução terrível. A análise revela essa diferença.
            </p>
          </section>

          {/* Seção 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <TrendUp size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Métricas Importantes no Trading</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Números contam a história real do seu trading. Sem métricas, você está operando baseado em sensações — e sensações mentem.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-gradient-to-br from-[#00FF85]/10 to-transparent border border-[#00FF85]/20 rounded-xl p-6">
                <h3 className="font-bold text-[#00FF85] text-lg mb-2">Win Rate</h3>
                <p className="text-gray-400 text-sm mb-3">Percentual de trades vencedores. Importante, mas não é tudo — um win rate de 40% pode ser lucrativo com bom RR.</p>
                <p className="text-xs text-gray-500">Fórmula: (Trades Vencedores / Total de Trades) × 100</p>
              </div>

              <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-xl p-6">
                <h3 className="font-bold text-purple-400 text-lg mb-2">Risk/Reward (RR)</h3>
                <p className="text-gray-400 text-sm mb-3">Relação entre o que você arrisca e o que busca ganhar. RR de 2:1 significa buscar 2x o risco.</p>
                <p className="text-xs text-gray-500">Fórmula: Ganho Médio / Perda Média</p>
              </div>

              <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 rounded-xl p-6">
                <h3 className="font-bold text-blue-400 text-lg mb-2">Profit Factor</h3>
                <p className="text-gray-400 text-sm mb-3">Razão entre ganhos totais e perdas totais. Acima de 1.5 é bom, acima de 2.0 é excelente.</p>
                <p className="text-xs text-gray-500">Fórmula: Soma dos Ganhos / Soma das Perdas</p>
              </div>

              <div className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-xl p-6">
                <h3 className="font-bold text-amber-400 text-lg mb-2">Consistência</h3>
                <p className="text-gray-400 text-sm mb-3">Variação dos resultados ao longo do tempo. Curva de capital suave indica trading consistente.</p>
                <p className="text-xs text-gray-500">Métrica: Desvio padrão dos retornos</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O trader profissional conhece essas métricas de cabeça. Se você não sabe seu win rate atual ou seu RR médio, está operando no escuro.
            </p>
          </section>

          {/* Seção 4 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <ListChecks size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Como Fazer uma Análise Passo a Passo</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Uma análise estruturada segue um processo. Não é apenas olhar o gráfico e pensar "foi bom" ou "foi ruim".
            </p>

            <div className="space-y-6 mb-6">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">1</div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Revisar o Contexto do Trade</h3>
                  <p className="text-gray-400 text-sm">Qual era o cenário do mercado? Havia notícia importante? O ativo estava em tendência ou lateralizado? Entenda o ambiente antes de julgar a decisão.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">2</div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Avaliar a Execução</h3>
                  <p className="text-gray-400 text-sm">Você seguiu seu plano? Entrou onde deveria? Saiu onde planejou? A execução foi o que diferencia um bom trade de um trade sorteado.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">3</div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Identificar Erros</h3>
                  <p className="text-gray-400 text-sm">Seja honesto. O que você fez errado? Entrou cedo? Moveu o stop? Não respeitou o plano? Liste os erros sem justificativas.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">4</div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Identificar Padrões</h3>
                  <p className="text-gray-400 text-sm">Depois de analisar vários trades, padrões aparecem. Você sempre erra em determinado horário? Certo setup funciona melhor? Padrões são ouro.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6">
              <p className="text-gray-300 text-sm">
                <strong className="text-[#00FF85]">Dica:</strong> Reserve um momento específico para análise. Pode ser no final do dia ou na manhã seguinte. O importante é criar o hábito.
              </p>
            </div>
          </section>

          {/* Seção 5 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Erros Comuns na Análise de Trades</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Mesmo traders que tentam analisar seus trades cometem erros que comprometem todo o processo.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Olhar Só Lucro/Prejuízo</h3>
                <p className="text-gray-400 text-sm">O resultado final não conta a história completa. Um trade vencedor pode ter sido mal executado (sorte), e um perdedor pode ter sido perfeito (azar).</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Ignorar Trades Perdedores</h3>
                <p className="text-gray-400 text-sm">É doloroso revisar perdas, mas é onde está o maior aprendizado. Fugir dos perdedores é fugir da evolução.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Não Revisar com Frequência</h3>
                <p className="text-gray-400 text-sm">Analisar uma vez por mês não funciona. A memória do trade se perde. Quanto mais próximo da operação, melhor a análise.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Falta de Consistência</h3>
                <p className="text-gray-400 text-sm">Analisar quando dá vontade não funciona. É preciso um processo sistemático e repetido para gerar resultados.</p>
              </div>
            </div>
          </section>

          {/* Seção 6 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Repeat size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">A Importância da Consistência</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Analisar um ou dois trades não muda nada. O valor está na repetição. É o acúmulo de análises que revela padrões, confirma hipóteses e gera evolução real.
            </p>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-red-400 mb-2">5 trades</div>
                  <p className="text-sm text-gray-500">Ruído estatístico. Impossível tirar conclusões.</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400 mb-2">30 trades</div>
                  <p className="text-sm text-gray-500">Começa a ter significância. Tendências aparecem.</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#00FF85] mb-2">100+ trades</div>
                  <p className="text-sm text-gray-500">Dados confiáveis. Padrões claros e acionáveis.</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Consistência na análise é tão importante quanto consistência na execução. Os dois andam juntos.
            </p>
          </section>

          {/* Seção 7 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Brain size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Psicologia na Análise</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A mente humana não é feita para análise objetiva. Nosso cérebro distorce memórias, justifica decisões ruins e evita dor emocional. Isso contamina a análise de trades.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Viés Emocional</h3>
                <p className="text-gray-400 text-sm">Você lembra dos trades de forma diferente dependendo do resultado. Ganhos parecem óbvios em retrospecto, perdas parecem azaradas.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Justificar Erros</h3>
                <p className="text-gray-400 text-sm">"O mercado estava manipulado", "Saiu notícia inesperada". Sempre existe uma justificativa externa. Raramente assumimos responsabilidade.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Dificuldade de Aceitar Perdas</h3>
                <p className="text-gray-400 text-sm">Revisar trades perdedores dói. Mas é exatamente nessa dor que está o crescimento. Evitar a dor é evitar a evolução.</p>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <BookOpen size={32} className="text-[#00FF85] flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white mb-2">Leitura Recomendada</h3>
                  <p className="text-gray-400 text-sm mb-2">
                    <strong className="text-white">"Trading in the Zone"</strong> de Mark Douglas explora profundamente a psicologia do trading e como a mente sabota nossos resultados. Leitura obrigatória para quem quer evoluir.
                  </p>
                  <p className="text-gray-500 text-xs">Douglas mostra que o sucesso no trading é 80% psicológico e 20% técnico.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Seção 8 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <TrendUp size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">De Trading Aleatório para Trading Estruturado</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A diferença entre um trader amador e um profissional não é talento — é estrutura. O amador opera baseado em feeling. O profissional opera baseado em dados.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-red-400 mb-4">Trading Aleatório</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>• Decisões baseadas em "feeling"</li>
                  <li>• Sem registro de trades</li>
                  <li>• Não sabe métricas próprias</li>
                  <li>• Repete erros indefinidamente</li>
                  <li>• Resultados inconsistentes</li>
                </ul>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6">
                <h3 className="font-semibold text-[#00FF85] mb-4">Trading Estruturado</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>• Decisões baseadas em dados</li>
                  <li>• Todo trade registrado</li>
                  <li>• Métricas acompanhadas</li>
                  <li>• Erros identificados e corrigidos</li>
                  <li>• Evolução mensurável</li>
                </ul>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              A evolução real vem de feedback. Sem feedback estruturado, você está apenas repetindo comportamentos sem saber se funcionam ou não. Dados transformam intuição em conhecimento.
            </p>
          </section>

          {/* GENI Mention */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent border border-[#00FF85]/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Organize Sua Análise com Ferramentas Certas</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Ferramentas como o <strong className="text-[#00FF85]">GENI</strong> ajudam a organizar e analisar trades automaticamente, facilitando a identificação de padrões e erros. Em vez de planilhas manuais que consomem tempo, você foca no que importa: melhorar.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline"
              >
                Experimentar GENI Grátis <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Comece a Analisar Seus Trades Hoje</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Se você quer evoluir no trading, analisar seus trades de forma consistente é essencial. Não existe atalho. A disciplina na análise separa quem melhora de quem fica parado.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
              >
                Começar Grátis <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Perguntas Frequentes</h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Como analisar trades corretamente?</h3>
                <p className="text-gray-400 text-sm">Registre cada trade com detalhes (entrada, saída, setup, RR), revise o contexto do mercado, avalie sua execução, identifique erros e busque padrões ao longo do tempo. A chave é consistência.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">O que devo analisar em um trade?</h3>
                <p className="text-gray-400 text-sm">Cinco elementos principais: entrada (timing e preço), saída (momento e motivo), risco/retorno (se era favorável), setup utilizado (e se foi seguido), e execução geral (se o plano foi respeitado).</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Qual a melhor forma de melhorar no trading?</h3>
                <p className="text-gray-400 text-sm">Combine prática deliberada com análise estruturada. Opere com plano definido, registre tudo, analise regularmente, identifique padrões de erro e ajuste. A melhoria vem do ciclo: executar → analisar → ajustar → repetir.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Preciso analisar todos os trades?</h3>
                <p className="text-gray-400 text-sm">Idealmente, sim. Cada trade contém informação. Se o volume for muito alto, priorize os trades mais significativos (grandes ganhos, grandes perdas, trades fora do plano). Mas quanto mais dados, melhor a análise.</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/pt/diario-de-trades-automatico"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Diário de Trades Automático</h3>
                <p className="text-gray-500 text-sm">Como automatizar o registro dos seus trades e economizar tempo.</p>
              </Link>

              <Link
                to="/pt/diario-de-trades-cripto"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Diário de Trades para Cripto</h3>
                <p className="text-gray-500 text-sm">Especificidades de um diário de trading para o mercado de criptomoedas.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/geni-logo.png" alt="GENI" className="h-6" />
              <span className="font-semibold">GENI</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 GENI. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComoAnalisarTrades;
